import React, { useState } from 'react';

import { ACCOUNT_ENTITY_TYPES, ACCOUNT_WORK_STATUS, accountYears } from './accountModel';
import { useAccountRecord } from './AccountRecordContext';
import { AccountPageShell } from './AccountPageShell';
import {
  AccountCard,
  CardColumn,
  Hint,
  NavLink,
  Row,
  SelectField,
} from './primitives';

/**
 * AR-380 — Your Business.
 *
 * v15 split Business + Brand in two: this page is the business ACCOUNT — the
 * entity's own facts (D-ACCT-30). The name, logo, tagline and description the
 * public reads moved to Brand + Public Presence.
 *
 * The page is the agency owner's. WorldVia holds no legal-entity data for an
 * associate advisor, because it has no agreement with an associate's entity, so
 * the id is owner-only and the card no longer has a second reading (A1/D-ACCT-39).
 * The legal entity name stays read-only for everyone: changing it needs a member
 * agreement, so the form shows it and routes to Member Success.
 *
 * The Business address card is gone — WorldVia holds no business address today,
 * so the page is one card (A2).
 */
export const AccountBusiness: React.FC = () => {
  const { record } = useAccountRecord();
  const b = record.biz;

  return (
    <AccountPageShell page="business">
      <CardColumn>
        <AccountCard
          cardKey="biz"
          name="Business details"
          form={
            <>
              <div>
                <span className="mb-1 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                  Legal entity name
                </span>
                <p className="mt-0.5 text-[15px] text-gray-900 dark:text-gray-100">
                  {b.legal}
                </p>
                <Hint>To change the legal entity name, contact Member Success.</Hint>
                <Hint>
                  <NavLink to="/help">Contact Member Success →</NavLink>
                </Hint>
              </div>
              <SelectField
                path="biz.type"
                label="Legal entity type"
                options={ACCOUNT_ENTITY_TYPES}
              />
              <SelectField
                path="biz.since"
                label="In business since"
                options={accountYears(1990, new Date().getFullYear())}
              />
              <SelectField
                path="biz.work"
                label="Business time allocation"
                options={ACCOUNT_WORK_STATUS}
              />
            </>
          }
        >
          <Row label="Legal entity name" value={b.legal} />
          <Row label="Legal entity type" value={b.type} />
          <Row label="In business since" value={b.since} />
          <Row label="Business time allocation" value={b.work} last />
        </AccountCard>
      </CardColumn>
    </AccountPageShell>
  );
};

export default AccountBusiness;
